import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { combineLatest } from "rxjs";
import { filter } from "rxjs/operators";
import { ScaleTime } from "d3";
import * as d3 from "d3";
import { getContrastTextColour } from "@iventis/utilities";
import { GroupScale } from "./group-axis";
import { SESSION_RECT_PADDING } from "./calendar-context";
import { Session, SessionComponent } from "./types";
import { useGroupScale, useTimeScale } from "./hooks";

const SUB_EVENT_MARGIN = 4;

interface SubEventProps {
    subEvent: Session;
    session: Session;
    bgColour: string;
    textColour: string;
    eventUnitComponent: SessionComponent;
    onClick: (subEvent: Session, event: PointerEvent) => void;
    onContextClick?: (subEvent: Session, event: PointerEvent) => void;
}

export const EventUnitComponent: React.FC<SubEventProps> = ({ session, subEvent, onClick, onContextClick, eventUnitComponent }) => {
    const bgColour = subEvent.colour.fill;
    const textColour = useMemo(() => getContrastTextColour(bgColour), [bgColour]);
    const ref = useRef<SVGGElement>();
    const timeScale$ = useTimeScale();
    const groupScale$ = useGroupScale();

    const clickHandler = useCallback(
        (event: React.MouseEvent<SVGGElement, Event>) => {
            onClick?.(subEvent, event.nativeEvent as PointerEvent);
            event.stopPropagation();
        },
        [subEvent, onClick]
    );

    const contextClickHandler = useCallback(
        (event) => {
            onContextClick?.(subEvent, event.nativeEvent);
            event.preventDefault();
            event.stopPropagation();
        },
        [subEvent, onContextClick]
    );

    useEffect(() => {
        const sub = combineLatest([timeScale$.pipe(filter<ScaleTime<number, number, never>>(Boolean)), groupScale$.pipe(filter<GroupScale>(Boolean))]).subscribe(
            ([timeScale, groupScale]) => {
                const start = subEvent.sessionStart;
                const end = subEvent.sessionEnd;
                const { sessionStart } = session;

                // Since the event unit start positioning is within the <g> of the session, we have to offset it by the start time of the session itself to get a relative value
                const timeAdjust = timeScale(sessionStart);

                const groupAdjust = groupScale.getGroupCoords(session.groupId)[0];
                const svg = d3.select(ref.current);
                const xRange = groupScale.getGroupCoords(subEvent.groupId);

                const y = timeScale(start) + 0.5 * SUB_EVENT_MARGIN;
                const x = xRange[0] + 0.5 * SUB_EVENT_MARGIN;
                const width = Math.max(xRange[1] - xRange[0], 0) - 4 * SUB_EVENT_MARGIN;
                const height = Math.max(Math.abs(timeScale(end) - y) - SUB_EVENT_MARGIN, 0);

                svg.attr("transform", `translate(${x - groupAdjust}, ${y - timeAdjust})`);
                svg.selectChildren().attr("width", width).attr("height", height);
            }
        );

        return () => sub.unsubscribe();
    }, [timeScale$, groupScale$, subEvent]);

    const EventUnitRenderer = eventUnitComponent;
    return (
        <g data-cy="calendar-sub-event" ref={ref} onClick={clickHandler} onContextMenu={contextClickHandler}>
            <rect fill={bgColour} stroke={subEvent.colour.outline} rx={4} x={SUB_EVENT_MARGIN} y={0} />
            <foreignObject x={SESSION_RECT_PADDING} y={0}>
                <EventUnitRenderer session={subEvent} contrastTextColour={textColour} />
            </foreignObject>
        </g>
    );
};
